import React from "react";
import styles from "./ErrorMessage.module.css";

/**
 * ErrorMessage Component - Compound Component Pattern
 * Displays error messages with retry functionality
 */
import { ApiError } from "../../../../utils/ApiError";

const ERROR_TYPES = {
    NETWORK: { emoji: "📡", title: "Problema de conexión", message: "Parece que no tienes internet. Revisa tu conexión e intenta nuevamente." },
    SERVER: { emoji: "💥", title: "Error del servidor", message: "Tuvimos un problema en nuestros servidores. Por favor, intenta más tarde." },
    CLIENT: { emoji: "🤔", title: "Algo salió mal", message: "No pudimos procesar tu solicitud. " },
    DEFAULT: { emoji: "⚠️", title: "Ocurrió un error", message: "Algo inesperado sucedió. Por favor, intenta nuevamente." }
};

const getErrorConfig = (error) => {
    if (!error) return ERROR_TYPES.DEFAULT;

    // Network error (fetch failure)
    if (error.message === "Failed to fetch" || error.message.includes("NetworkError")) {
        return ERROR_TYPES.NETWORK;
    }

    if (error instanceof ApiError || error.status) {
        if (error.status >= 500) return ERROR_TYPES.SERVER;
        if (error.status >= 400) return {
            ...ERROR_TYPES.CLIENT,
            message: error.message || ERROR_TYPES.CLIENT.message
        };
    }

    return { ...ERROR_TYPES.DEFAULT, message: error.message || ERROR_TYPES.DEFAULT.message };
};

/**
 * ErrorMessage Component - Compound Component Pattern
 * Displays error messages with retry functionality
 */
const ErrorMessage = ({ children, error }) => {
    // If error prop is provided, we can auto-configure the children or context but 
    // for this pattern, let's allow children to override or use context if we added it.
    // For now, we'll expose a helper static method to render the full error state easily
    // or keep the compound pattern. 
    // Let's deduce config to pass to children if they are clones.

    return <div className={styles.container}>{children}</div>;
};

// Compound components
ErrorMessage.Icon = ({ children, error }) => {
    const config = getErrorConfig(error);
    return <div className={styles.icon}>{children || config.emoji}</div>;
};

ErrorMessage.Title = ({ children, error }) => {
    const config = getErrorConfig(error);
    return <h2 className={styles.title}>{children || config.title}</h2>;
};

ErrorMessage.Message = ({ children, error }) => {
    const config = getErrorConfig(error);
    return <p className={styles.message}>{children || config.message}</p>;
};
ErrorMessage.Actions = ({ children }) => {
    return <div className={styles.actions}>{children}</div>;
};

ErrorMessage.RetryButton = ({ onClick, children = "Reintentar" }) => {
    return (
        <button className={styles.retryButton} onClick={onClick}>
            {children}
        </button>
    );
};

export default ErrorMessage;
